"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = TOCInstanceComp;
const lodash_1 = __importDefault(require("lodash"));
const toc_1 = require("./toc");
const blocks_1 = require("../../blocks");
const react_1 = require("react");
const localization_1 = require("../../localization");
const SplitPane_1 = __importDefault(require("./SplitPane"));
const react_2 = __importDefault(require("react"));
const PageStackDisplay_1 = require("../../../PageStackDisplay");
const contexts_1 = require("../../../contexts");
const expressions_1 = require("@mwater/expressions");
const FillDownwardComponent_1 = __importDefault(require("@mwater/react-library/lib/FillDownwardComponent"));
const hooks_1 = require("../../../hooks");
const embeddedExprs_1 = require("../../../embeddedExprs");
/** Instance component for TOC */
function TOCInstanceComp(props) {
    const { blockDef, instanceCtx } = props;
    // Store overall page width and update it
    const pageWidth = (0, hooks_1.usePageWidth)();
    // Ref to page stack to ensure closed properly
    const pageStackRef = (0, react_1.useRef)(null);
    const allItems = (0, toc_1.iterateItems)(blockDef.items);
    // Select first non-repeating item with widget by default
    const firstItem = allItems.find((item) => item.widgetId && !item.repeatRowsetContextVarId);
    const [selectedId, setSelectedId] = (0, react_1.useState)(firstItem ? firstItem.id : null);
    // Store selected row where repeating
    const [selectedRowId, setSelectedRowId] = (0, react_1.useState)(null);
    // When TOC selector is open in collapsed mode
    const [selectorOpen, setSelectorOpen] = (0, react_1.useState)(false);
    // Select item
    function handleItemSelected(item, rowId) {
        // Only allow selecting with content
        if (!item.widgetId) {
            return;
        }
        // Do nothing if same id and row
        if (item.id == selectedId && rowId == selectedRowId) {
            return;
        }
        // Close all pages
        if (pageStackRef.current) {
            if (!pageStackRef.current.closeAllPages()) {
                return;
            }
        }
        setSelectedId(item.id);
        setSelectedRowId(rowId);
        setSelectorOpen(false);
    }
    function renderLeft() {
        return (react_2.default.createElement("div", { className: "toc-left-container" },
            react_2.default.createElement("div", { className: "toc-header" }, instanceCtx.renderChildBlock(instanceCtx, blockDef.header)),
            react_2.default.createElement("div", { className: "toc-items" }, blockDef.items.map((item) => (react_2.default.createElement(TOCItemComponent, { key: item.id, item: item, instanceCtx: instanceCtx, selectedId: selectedId, selectedRowId: selectedRowId, depth: 0, pageWidth: pageWidth, rowId: null, onItemSelected: handleItemSelected })))),
            react_2.default.createElement("div", { className: "toc-footer" }, instanceCtx.renderChildBlock(instanceCtx, blockDef.footer))));
    }
    // Get selected item
    const selectedAncestry = selectedId ? (0, toc_1.getItemAncestry)((0, toc_1.iterateItems)(blockDef.items), selectedId) : [];
    const selectedItem = selectedAncestry.length > 0 ? selectedAncestry[selectedAncestry.length - 1] : null;
    const selectedWidgetId = selectedItem ? selectedItem.widgetId : null;
    function getTitle(item) {
        if (!item.title) {
            return undefined;
        }
        // Get any embedded expression values
        const exprValues = lodash_1.default.map(item.titleEmbeddedExprs || [], (ee) => instanceCtx.getContextVarExprValue(ee.contextVarId, ee.expr));
        // Format and replace
        let title = (0, localization_1.localize)(item.title, instanceCtx.locale);
        title = (0, embeddedExprs_1.formatEmbeddedExprString)({
            text: title,
            embeddedExprs: item.titleEmbeddedExprs || [],
            exprValues: exprValues,
            schema: instanceCtx.schema,
            contextVars: instanceCtx.contextVars,
            locale: instanceCtx.locale,
            formatLocale: instanceCtx.formatLocale,
            currency: instanceCtx.currency
        });
        return title;
    }
    /** Render the right pane (or only pane if collapsed)
     * @param noTitle do not render title even if item has one
     */
    function renderRight(noTitle) {
        if (!selectedId || !selectedWidgetId || !selectedItem || selectedAncestry.length == 0) {
            return null;
        }
        // Get widget
        const widget = instanceCtx.widgetLibrary.widgets[selectedWidgetId];
        // Map context var values
        const mappedContextVarValues = {};
        // Get context vars for right pane
        const contextVars = [...instanceCtx.contextVars];
        const contextVarValues = { ...instanceCtx.contextVarValues };
        // If any repeating items in ancestry, add row context variable
        for (const item of selectedAncestry) {
            if (item.repeatRowsetContextVarId) {
                const repeatRowsetContextVar = instanceCtx.contextVars.find(cv => cv.id === item.repeatRowsetContextVarId);
                const repeatRowContextVar = (0, toc_1.createTOCItemContextVar)(repeatRowsetContextVar);
                contextVars.push(repeatRowContextVar);
                contextVarValues[repeatRowContextVar.id] = selectedRowId;
            }
        }
        // For each context variable that the widget needs
        for (const innerContextVar of widget.contextVars) {
            const outerContextVarId = (selectedItem.contextVarMap || {})[innerContextVar.id];
            if (outerContextVarId) {
                // Look up outer context variable
                const outerCV = contextVars.find((cv) => cv.id == outerContextVarId);
                if (!outerCV) {
                    throw new Error("Outer context variable not found");
                }
                // Get value
                let outerCVValue = contextVarValues[outerCV.id];
                // Add filters if rowset
                if (outerCV.type == "rowset") {
                    outerCVValue = {
                        type: "op",
                        op: "and",
                        table: outerCV.table,
                        exprs: lodash_1.default.compact([outerCVValue].concat(lodash_1.default.map(instanceCtx.getFilters(outerCV.id), (f) => f.expr)))
                    };
                }
                // Inline variables used in rowsets as they may depend on context variables that aren't present in new page
                if (outerCV.type == "rowset") {
                    outerCVValue = new expressions_1.ExprUtils(instanceCtx.schema.addVariables((0, blocks_1.createExprVariables)(instanceCtx.contextVars), {})).inlineVariableValues(outerCVValue, (0, blocks_1.createExprVariableValues)(instanceCtx.contextVars, instanceCtx.contextVarValues));
                }
                mappedContextVarValues[innerContextVar.id] = outerCVValue;
            }
            else {
                mappedContextVarValues[innerContextVar.id] = null;
            }
        }
        // Include global context variables
        for (const globalContextVar of props.instanceCtx.globalContextVars || []) {
            mappedContextVarValues[globalContextVar.id] = props.instanceCtx.contextVarValues[globalContextVar.id];
        }
        const page = {
            contextVarValues: mappedContextVarValues,
            database: instanceCtx.database,
            type: "normal",
            title: !noTitle ? getTitle(selectedItem) : undefined,
            widgetId: selectedWidgetId
        };
        // Create page stack
        return react_2.default.createElement(PageStackDisplay_1.PageStackDisplay, { key: selectedId + ":" + (selectedRowId || ""), baseCtx: props.instanceCtx, initialPage: page, ref: pageStackRef });
    }
    // If below minimum, use collapsed view
    if (blockDef.collapseWidth != null && pageWidth <= blockDef.collapseWidth) {
        if (selectedId == null) {
            return react_2.default.createElement("div", null);
        }
        const selectedItem = allItems.find((item) => item.id == selectedId);
        if (!selectedItem) {
            return react_2.default.createElement("div", null);
        }
        const title = getTitle(selectedItem);
        return (react_2.default.createElement("div", { onClick: () => setSelectorOpen(false) },
            react_2.default.createElement(FillDownwardComponent_1.default, null,
                react_2.default.createElement("div", { key: "header" },
                    react_2.default.createElement("div", { key: "selected", onClick: (ev) => {
                            ev.stopPropagation();
                            setSelectorOpen((v) => !v);
                        }, className: `toc-select-button${selectorOpen ? " open" : ""}` },
                        react_2.default.createElement("i", { className: "fa fa-bars" })),
                    " ",
                    react_2.default.createElement("div", { className: "toc-select-title" }, title),
                    selectorOpen ? react_2.default.createElement("div", { className: "toc-selector" }, renderLeft()) : null),
                renderRight(true))));
    }
    // Render overall structure
    return react_2.default.createElement(SplitPane_1.default, { left: renderLeft(), right: renderRight(false), theme: blockDef.theme || "light" });
}
function TOCItemComponent(props) {
    if (!props.item.repeatRowsetContextVarId) {
        return react_2.default.createElement(SimpleTOCItemComponent, { ...props });
    }
    return react_2.default.createElement(RepeatingTOCItemComponent, { ...props });
}
/**
 * TOC item without repeating
 */
function SimpleTOCItemComponent(props) {
    const { item, instanceCtx, selectedId, selectedRowId, depth, pageWidth, rowId, onItemSelected } = props;
    const [collapsed, setCollapsed] = (0, react_1.useState)(item.collapse == "startCollapsed" ||
        (item.collapse == "startExpanded" && item.collapseWidth != null && pageWidth <= item.collapseWidth));
    // Determine if visible
    if (item.condition && item.condition.expr) {
        const conditionValue = instanceCtx.getContextVarExprValue(item.condition.contextVarId, item.condition.expr);
        if (conditionValue !== true) {
            return null;
        }
    }
    function renderItem(item) {
        // Legacy support of label
        if (item.label != null) {
            return react_2.default.createElement("div", null, (0, localization_1.localize)(item.label, instanceCtx.locale));
        }
        return instanceCtx.renderChildBlock(instanceCtx, item.labelBlock || null);
    }
    function handleItemClick() {
        handleItemToggle();
        onItemSelected(item, rowId);
    }
    function handleItemToggle() {
        if (item.children.length > 0 && (item.collapse == "startCollapsed" || item.collapse == "startExpanded")) {
            setCollapsed(!collapsed);
        }
    }
    const collapsible = item.children.length > 0 && (item.collapse == "startCollapsed" || item.collapse == "startExpanded");
    return (react_2.default.createElement(TOCItemDisplay, { item: item, depth: depth, selected: selectedId === item.id && selectedRowId === rowId, collapsed: collapsed, collapsible: collapsible, itemNode: renderItem(item), onItemClick: handleItemClick, onItemToggle: handleItemToggle }, item.children.length > 0 && !collapsed && (react_2.default.createElement("div", { key: "children", className: "toc-item-children" }, item.children.map((child) => (react_2.default.createElement(TOCItemComponent, { key: child.id, item: child, instanceCtx: instanceCtx, selectedId: selectedId, selectedRowId: selectedRowId, depth: depth + 1, pageWidth: pageWidth, rowId: rowId, onItemSelected: onItemSelected })))))));
}
/**
 * TOC item with repeating. This is a type of item where the item is repeated for each row in a rowset.
 */
function RepeatingTOCItemComponent(props) {
    const { item, instanceCtx, selectedId, selectedRowId, depth, pageWidth, onItemSelected } = props;
    // Determine if visible
    if (item.condition && item.condition.expr) {
        const conditionValue = instanceCtx.getContextVarExprValue(item.condition.contextVarId, item.condition.expr);
        if (conditionValue !== true) {
            return null;
        }
    }
    const [rows, setRows] = (0, react_1.useState)([]);
    const [refreshing, setRefreshing] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    /** Get expressions that are needed for the row and all child items */
    function getContextVarExprs(item, rowCV) {
        const rowExprs = [];
        const block = instanceCtx.createBlock(item.labelBlock);
        rowExprs.push(...block.getContextVarExprs(rowCV, instanceCtx));
        for (const child of item.children) {
            rowExprs.push(...getContextVarExprs(child, rowCV));
        }
        return rowExprs;
    }
    /** Create query for repeating item */
    function createQuery() {
        const rips = instanceCtx;
        // Get expressions
        const rowsetCV = rips.contextVars.find((cv) => cv.id === item.repeatRowsetContextVarId);
        // Get row context variable
        const rowCV = (0, toc_1.createTOCItemContextVar)(rowsetCV);
        // Get expressions that are needed for the row and all child items
        const rowExprs = getContextVarExprs(item, rowCV);
        // Get value of rowset context variable which is used to filter the rowset
        const rowsetCVValue = rips.contextVarValues[rowsetCV.id];
        // Create where
        const where = {
            type: "op",
            op: "and",
            table: rowsetCV.table,
            exprs: lodash_1.default.compact([rowsetCVValue].concat(lodash_1.default.map(rips.getFilters(rowsetCV.id), (f) => f.expr)))
        };
        // Add own where
        if (item.repeatWhere) {
            where.exprs.push(item.repeatWhere);
        }
        let queryOptions = {
            select: {},
            from: rowsetCV.table,
            where: where.exprs.length > 0 ? where : null,
            orderBy: [],
            limit: item.repeatLimit
        };
        // Add order by
        if (item.repeatOrderBy) {
            queryOptions.orderBy = queryOptions.orderBy.concat(item.repeatOrderBy);
        }
        // Stabilize sort order
        queryOptions.orderBy.push({ expr: { type: "id", table: rowsetCV.table }, dir: "asc" });
        // Add expressions
        queryOptions.select.id = { type: "id", table: rowsetCV.table };
        rowExprs.forEach((expr, index) => {
            queryOptions.select["e" + index] = expr;
        });
        // The context variable that represents the row has a value which changes with each row
        // so replace it with { type: "id" ...} expression so that it evaluates as the row id
        queryOptions = mapObject(queryOptions, (input) => {
            if (input && input.type == "variable" && input.variableId == rowCV.id) {
                return { type: "id", table: queryOptions.from };
            }
            return input;
        });
        return queryOptions;
    }
    const [refreshCounter, setRefreshCounter] = (0, react_1.useState)(0);
    // Query rows and handle database changes
    (0, react_1.useEffect)(() => {
        const handleChange = () => {
            setRefreshCounter(prev => prev + 1);
        };
        const performQuery = () => {
            setRefreshing(true);
            setError(null);
            setRows([]);
            const query = createQuery();
            instanceCtx.database.query(query, instanceCtx.contextVars, (0, contexts_1.getFilteredContextVarValues)(instanceCtx))
                .then((rows) => {
                setRows(rows);
                setRefreshing(false);
            })
                .catch((error) => {
                setError(error);
                setRefreshing(false);
            });
        };
        // Add change listener
        instanceCtx.database.addChangeListener(handleChange);
        // Perform initial query
        performQuery();
        // Clean up listener on unmount
        return () => {
            instanceCtx.database.removeChangeListener(handleChange);
        };
    }, [item, refreshCounter]);
    function createRowInstanceCtx(row) {
        // Row context variable
        const rowsetCV = instanceCtx.contextVars.find((cv) => cv.id === item.repeatRowsetContextVarId);
        const rowCV = (0, toc_1.createTOCItemContextVar)(rowsetCV);
        // TODO move out of here to be faster
        const rowExprs = getContextVarExprs(item, rowCV);
        const innerContextVars = instanceCtx.contextVars.concat(rowCV);
        // Row context variable value
        const innerContextVarValues = { ...instanceCtx.contextVarValues, [rowCV.id]: row.id };
        return {
            ...instanceCtx,
            contextVars: innerContextVars,
            contextVarValues: innerContextVarValues,
            getContextVarExprValue: (cvid, expr) => {
                // Null expression has null value
                if (!expr) {
                    return null;
                }
                // If no context variable, evaluate expression
                if (cvid == null) {
                    return new expressions_1.PromiseExprEvaluator({
                        schema: instanceCtx.schema.addVariables((0, blocks_1.createExprVariables)(innerContextVars), (0, blocks_1.createExprVariableValues)(innerContextVars, innerContextVarValues)),
                        locale: instanceCtx.locale,
                    }).evaluateSync(expr);
                }
                if (cvid !== rowCV.id) {
                    return instanceCtx.getContextVarExprValue(cvid, expr);
                }
                // Look up expression
                const exprIndex = rowExprs.findIndex((rowExpr) => lodash_1.default.isEqual(expr, rowExpr));
                return row["e" + exprIndex];
            }
        };
    }
    function renderRow(row) {
        // Create a new instance context for the row
        return react_2.default.createElement(RepeatingTOCItemRow, { key: row.id, item: item, depth: depth, selectedId: selectedId, selectedRowId: selectedRowId, pageWidth: pageWidth, rowId: row.id, onItemSelected: onItemSelected, instanceCtx: createRowInstanceCtx(row) });
    }
    if (refreshing) {
        return react_2.default.createElement(TOCItemDisplay, { item: item, depth: depth, selected: selectedId === item.id, collapsed: true, collapsible: false, itemNode: react_2.default.createElement("span", { className: "placeholder col-6" }), onItemClick: () => { }, onItemToggle: () => { } });
    }
    if (error) {
        return react_2.default.createElement("div", null,
            "Error: ",
            error.message);
    }
    return react_2.default.createElement(react_2.default.Fragment, null, rows.map((row) => renderRow(row)));
}
/**
 * TOC item with repeating single row. One of these is created for each row in the rowset.
 */
function RepeatingTOCItemRow(props) {
    const { item, instanceCtx, selectedId, selectedRowId, depth, pageWidth, rowId, onItemSelected } = props;
    const [collapsed, setCollapsed] = (0, react_1.useState)(item.collapse == "startCollapsed" ||
        (item.collapse == "startExpanded" && item.collapseWidth != null && pageWidth <= item.collapseWidth));
    // Determine if visible
    if (item.condition && item.condition.expr) {
        const conditionValue = instanceCtx.getContextVarExprValue(item.condition.contextVarId, item.condition.expr);
        if (conditionValue !== true) {
            return null;
        }
    }
    function renderItem(item) {
        // Legacy support of label
        if (item.label != null) {
            return react_2.default.createElement("div", null, (0, localization_1.localize)(item.label, instanceCtx.locale));
        }
        return instanceCtx.renderChildBlock(instanceCtx, item.labelBlock || null);
    }
    function handleItemClick() {
        handleItemToggle();
        onItemSelected(item, rowId);
    }
    function handleItemToggle() {
        if (item.children.length > 0 && (item.collapse == "startCollapsed" || item.collapse == "startExpanded")) {
            setCollapsed(!collapsed);
        }
    }
    const collapsible = item.children.length > 0 && (item.collapse == "startCollapsed" || item.collapse == "startExpanded");
    return (react_2.default.createElement(TOCItemDisplay, { item: item, depth: depth, selected: selectedId === item.id && selectedRowId === rowId, collapsed: collapsed, collapsible: collapsible, itemNode: renderItem(item), onItemClick: handleItemClick, onItemToggle: handleItemToggle }, item.children.length > 0 && !collapsed && (react_2.default.createElement("div", { key: "children", className: "toc-item-children" }, item.children.map((child) => (react_2.default.createElement(TOCItemComponent, { key: child.id, item: child, instanceCtx: instanceCtx, selectedId: selectedId, selectedRowId: selectedRowId, depth: depth + 1, pageWidth: pageWidth, rowId: rowId, onItemSelected: onItemSelected })))))));
}
function TOCItemDisplay(props) {
    const { item, depth, selected, collapsed, collapsible, onItemClick, onItemToggle, children, itemNode } = props;
    const labelClasses = ["toc-item-label", `toc-item-label-level${depth}`];
    if (selected) {
        labelClasses.push(`toc-item-label-selected`);
    }
    if (item.widgetId || collapsible) {
        labelClasses.push("toc-item-label-selectable");
    }
    const handleItemClick = (ev) => {
        ev.stopPropagation();
        onItemClick();
    };
    const handleItemToggle = (ev) => {
        ev.stopPropagation();
        onItemToggle();
    };
    return (react_2.default.createElement("div", { className: `toc-item toc-item-level${depth}` },
        react_2.default.createElement("div", { className: labelClasses.join(" "), onClick: handleItemClick },
            itemNode,
            react_2.default.createElement("div", { className: "chevron", onClick: handleItemToggle, style: { visibility: collapsible ? "visible" : "hidden", opacity: 0.25 } }, collapsible ? (collapsed ? (react_2.default.createElement("i", { className: "fas fa-fw fa-chevron-right" })) : (react_2.default.createElement("i", { className: "fas fa-fw fa-chevron-down" }))) : (react_2.default.createElement("i", { className: "fas fa-fw fa-chevron-right" })))),
        children));
}
/** Replace every part of an object, including array members
 * replacer should return input to leave unchanged
 */
const mapObject = (obj, replacer) => {
    obj = replacer(obj);
    if (!obj) {
        return obj;
    }
    if (lodash_1.default.isArray(obj)) {
        return lodash_1.default.map(obj, (item) => mapObject(item, replacer));
    }
    if (lodash_1.default.isObject(obj)) {
        return lodash_1.default.mapValues(obj, (item) => mapObject(item, replacer));
    }
    return obj;
};
